import { Fragment, useState } from 'react'
import { useContentLocale } from '../../lib/content-locale'
import { Link, useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { pathInLocale, useTranslation } from '@lb/i18n'
import {
  isStaff,
  useAnnouncementTicker,
  useChallenge,
  useMyCohort,
  useNotifications,
  type PlatformRole,
} from '@lb/domain'
import {
  BottomSheet,
  IconBell,
  IconBoat,
  IconBook,
  IconCalendar,
  IconCommunity,
  IconCompass,
  IconHeadphones,
  IconCrew,
  IconHome,
  IconMegaphone,
  IconMessage,
  IconRobot,
  IconSearch,
  IconStar4,
  IconTornado,
  Logo,
  cn,
} from '@lb/ui'

import { http } from '../../lib/client'
import { CrewPanel } from './crew-panel'
import { FeedbackDialog } from './feedback-dialog'
import { NotificationsPanel } from './notifications-panel'
import { useHiddenNavPaths } from './locked-surfaces'
import { useSessionContext } from '../providers/session'
import {
  bottomTabsFor,
  moreItemsFor,
  navFor,
  SURFACE,
  type NavIcon,
  type NavItem,
} from './nav'
import { InstallPrompt } from './install-prompt'
import { ProfileMenu } from './profile-menu'
import { CompactLanguageToggle } from './language-toggle'
import { RouteAnnouncer } from './route-announcer'
import { SurfacePanel } from './surface-panel'
import { ThemePill } from './theme-pill'
import { NoIndex } from './seo'

/**
 * The shell — sidebar and top bar on desktop, bottom tabs and a More sheet on
 * mobile (§5.3).
 *
 * One component tree, two chromes. The alternative — a desktop tree and a
 * mobile tree behind a media query — is how the two drift, and the design asks
 * for parity rather than two products.
 */


const ICONS: Record<NavIcon, typeof IconHome> = {
  home: IconHome,
  boat: IconBoat,
  star4: IconStar4,
  book: IconBook,
  headphones: IconHeadphones,
  message: IconMessage,
  compass: IconCompass,
  calendar: IconCalendar,
  community: IconCommunity,
  robot: IconRobot,
}

export interface AppShellProps {
  role: PlatformRole
  /** The active route path, for the current state. */
  pathname: string
  children: ReactNode
  /** Rendered under the nav: My Crew (Parent), Licence (Pro), Your program (TSP). */
  aside?: ReactNode
  onNavigate: (to: string) => void
  onOpenStorm: () => void
}

export function AppShell({
  role,
  pathname,
  children,
  aside,
  onNavigate,
  onOpenStorm,
}: AppShellProps) {
  const { t } = useTranslation()
  const [moreOpen, setMoreOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  /* Less what this member cannot open yet — see `useHiddenNavPaths`. */
  const hidden = useHiddenNavPaths(role)
  /* Anybody with back-office access sees every section, whatever their role (8 Oct 2026). */
  const staff = isStaff(useSessionContext().viewer?.session)
  const nav = navFor(role, hidden, staff)
  const tabs = bottomTabsFor(role, hidden, staff)
  const more = moreItemsFor(role, hidden, staff)

  return (
    <div className="min-h-dvh bg-canvas md:grid md:grid-cols-[var(--sidebar-w)_1fr]">
      {/* ── Sidebar (desktop) ───────────────────────────────────────────── */}
      {/*
       * Sticky and viewport-height, with the nav scrolling inside it.
       *
       * This was `md:flex md:flex-col` with no height, so the grid row sized
       * the sidebar to whatever the MAIN COLUMN needed — 1531px on Journey —
       * and `mt-auto` pinned the footer to the bottom of a box that was mostly
       * below the fold. The language and theme controls were in the DOM and
       * unreachable on every long page, which is most of them.
       *
       * `h-dvh` not `h-screen`: on mobile browsers the toolbar makes `100vh`
       * taller than what you can actually see, which is the same bug again one
       * layer down.
       */}
      <aside className="hidden border-r border-border bg-surface px-[18px] py-5 md:sticky md:top-0 md:flex md:h-dvh md:flex-col">
        <button
          onClick={() => onNavigate('/')}
          className="mb-6 flex items-center gap-2 self-start"
          aria-label={t('brand.name')}
        >
          <Logo size={30} surface={SURFACE[role]?.chip} />
        </button>

        {/* The scrolling region. A long nav scrolls; the footer never moves. */}
        <div className="-mx-[18px] min-h-0 flex-1 overflow-y-auto px-[18px]">
          <nav className="flex flex-col gap-0.5" aria-label={t('nav.mainNav')}>
            {nav.map((item) => (
              <Fragment key={item.to}>
                <NavLink
                  item={item}
                  active={isActive(pathname, item.to)}
                  onClick={() => onNavigate(item.to)}
                  /*
                   * The design puts the cohort month on the nav item — `M6`.
                   *
                   * Passed as a NODE rather than added to `NAV_BY_ROLE`, because
                   * the badge needs a query and the nav table is a static record
                   * whose whole point is that adding a platform role fails to
                   * compile until somebody decides what it sees. A `badgeKey`
                   * column would teach that table about exceptions; a component
                   * that mounts only on the one item that has one does not, and
                   * costs nothing for the roles whose nav has no `/cohort`.
                   */
                  badge={
                    item.to === '/cohort' ? (
                      <CohortMonthBadge />
                    ) : item.to === '/challenge' ? (
                      <ChallengeBadge />
                    ) : null
                  }
                />
                {/*
                 * The rooms rail nests DIRECTLY under Community, per the mock:
                 * an indented column with a hairline at its left, not a block
                 * parked at the bottom of the sidebar under My Crew. `aside`
                 * only exists on Community routes (the router decides), so
                 * this renders nothing anywhere else.
                 */}
                {item.to === '/community' && aside ? (
                  <div className="mb-2 ml-[21px] mt-1.5 border-l-2 border-cream-darker pl-2.5">
                    {aside}
                  </div>
                ) : null}
              </Fragment>
            ))}
          </nav>

          {/* What your standing is on THIS surface — a licence for Pro, a
              cohort for TSP, nothing for a parent. */}
          <div className="mt-6">
            <SurfacePanel role={role} />
          </div>

          {/*
           * My Crew is parent-only — practitioners and TSP have their own
           * SurfacePanel in the block above, and an org admin has seat
           * administration. A crew widget on those roles would be an empty
           * heading, which reads as broken; better to not render at all.
           */}
          {role === 'PARENT' || staff ? (
            <div className="mt-6">
              <CrewPanel onNavigate={onNavigate} />
            </div>
          ) : null}
        </div>

        {/*
         * The rail's footer, pinned to the bottom of the sidebar. Order —
         * announcements → org-seat admin (only for that role) → profile pill
         * (opens a menu with language, Sign out, Privacy / Terms — see
         * `profile-menu.tsx`) → install-app prompt → segmented Light / Dark
         * pill — matches the approved mock. Reported: Announcements used to
         * sit in the scrollable nav region, leaving a huge gap above the
         * profile; pinning it here keeps the footer stack tight against the
         * bottom.
         */}
        <div className="shrink-0 border-t border-border-soft pt-3">
          <nav className="mb-3 flex flex-col gap-0.5" aria-label={t('nav.secondary')}>
            <SecondaryLink
              to="/announcements"
              icon={<IconMegaphone size={19} title={null} />}
              label={t('nav.announcements')}
              active={isActive(pathname, '/announcements')}
              onNavigate={onNavigate}
            />
            {role === 'ORG_ADMIN' ? (
              <SecondaryLink
                to="/seats"
                icon={<IconCrew size={19} title={null} />}
                label={t('nav.seats')}
                active={isActive(pathname, '/seats')}
                onNavigate={onNavigate}
              />
            ) : null}
          </nav>
          <ProfileMenu onNavigate={onNavigate} />
          {/*
           * `.sidebar-feedback` — between the profile pill and the theme
           * toggle, exactly where the mock puts it.
           *
           * `InstallPrompt` used to sit in this slot. It is not in the mock at
           * all, and it was a three-line block of explanatory copy ("A shortcut
           * on your home screen. Nothing is downloaded for offline use.") in
           * the densest part of the rail — permanently, whether or not the
           * browser could even install. It moves to the More sheet, where a
           * one-off setup action belongs and where there is room for the
           * sentence explaining it.
           */}
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="mt-1.5 flex min-h-[36px] w-full items-center gap-2 rounded-[var(--r-sm)] px-3 py-2 text-left text-[12.5px] font-medium text-muted transition-colors hover:bg-cream-dark hover:text-ink"
          >
            <IconMessage size={14} title={null} />
            {t('nav.feedback')}
          </button>
          <ThemePill />
        </div>
      </aside>

      {/* ── Canvas ──────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-col">
        <NoIndex />
        <RouteAnnouncer />
        <TopBar onOpenStorm={onOpenStorm} />

        <main
          id="main"
          /* Focusable programmatically, never in the tab order — see
             RouteAnnouncer, and the skip link that also targets it. */
          tabIndex={-1}
          /*
           * The bottom padding is ONE declaration, and it has to be.
           *
           * This was an inline `paddingBottom: calc(var(--consent-h, 0px))`
           * beside `pb-[120px] md:pb-24` in the class list — and an inline style
           * beats a class, so the computed value was 0px and both responsive
           * paddings were dead. The page then ended flush with the viewport,
           * with the fixed Storm control and the mobile tab bar sitting on the
           * last ~70px of it and nothing to scroll past. Measured, not guessed:
           * `getComputedStyle(main).paddingBottom` was "0px" on every route.
           *
           * `--consent-h` is 0 unless the banner is up (see consent-banner.tsx),
           * so this reads as the base padding until it is not.
           */
          className="mx-auto w-full max-w-[1280px] px-[18px] pt-[22px] pb-[calc(120px+var(--consent-h,0px))] md:px-9 md:pt-8 md:pb-[calc(96px+var(--consent-h,0px))] lg:px-14"
        >
          {children}
        </main>
      </div>

      {/* ── Bottom tabs (mobile) ────────────────────────────────────────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label={t('nav.mainNav')}
      >
        {tabs.map((item) => (
          <TabLink
            key={item.to}
            item={item}
            active={isActive(pathname, item.to)}
            onClick={() => onNavigate(item.to)}
          />
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          /* 44px minimum target, from the handoff's accessibility notes. */
          className="flex min-h-[50px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-muted"
        >
          <IconCompass size={20} />
          <span className="text-[10px]">{t('nav.more')}</span>
        </button>
      </nav>

      <BottomSheet open={moreOpen} onOpenChange={setMoreOpen} title={t('nav.more')}>
        {/*
         * `.more-grid` — icon tiles, two across, not a stacked list.
         *
         * The sheet holds everything the four bottom tabs could not, which for a
         * parent is six sections. As a list that is a scroll on a small phone;
         * as a grid it is one glance, which is the whole reason the design put
         * the overflow behind a sheet rather than a longer bar.
         */}
        <div className="mt-4 pb-4">
          <div className="grid grid-cols-2 gap-2">
            {more.map((item) => (
              <MoreTile
                key={item.to}
                item={item}
                active={isActive(pathname, item.to)}
                onClick={() => {
                  setMoreOpen(false)
                  onNavigate(item.to)
                }}
              />
            ))}
          </div>
          <div className="mt-3 border-t border-border-soft pt-3">
            {/*
             * Mirrors the desktop rail's footer, in the same order:
             * profile pill → install prompt → theme pill. Sign out,
             * language and legal links live behind the profile pill
             * rather than as separate strips here — the mobile More
             * sheet is where these were most stacked, and the
             * profile-menu popover collapses them into one press.
             */}
            <ProfileMenu
              onNavigate={(to) => {
                setMoreOpen(false)
                onNavigate(to)
              }}
            />
            {/* `Share feedback about La Boussole` — the sheet spells the
                target out where the rail's 220px could not (mock line 1061). */}
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false)
                setFeedbackOpen(true)
              }}
              className="flex min-h-[44px] w-full items-center gap-2.5 rounded-[var(--r-sm)] px-3 py-2 text-left text-[13.5px] font-medium text-ink-soft transition-colors hover:bg-cream-dark hover:text-ink"
            >
              <IconMessage size={16} title={null} />
              {t('nav.feedbackLong')}
            </button>
            <InstallPrompt />
            <ThemePill />
          </div>
        </div>
      </BottomSheet>

      {/*
       * The floating Storm button. The handoff: it hides whenever the More
       * sheet or any overlay is open — a parent reaching for the SOS must not
       * find it behind a menu.
       */}
      {/*
       * `.tempete-fab` — an INK pill carrying its own label on desktop, and a
       * round icon on mobile where a labelled pill would cover content.
       *
       * ── Also hidden on account-management screens ────────────────────────
       * `/profile` and `/billing/*` are settings surfaces, not moments of
       * distress — and their sticky drawer footers (Save/Cancel on the crew
       * editor, sticky "Switch to {plan}" bar on change-plan) sit exactly
       * where this FAB draws. On top of those, "Save" was unreachable
       * because the pill overlayed it. The SOS still exists on those
       * screens: the top-bar Storm pill is visible on every viewport. Hide
       * the FAB, keep the topbar entry.
       */}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      {!moreOpen && !isSettingsPath(pathname) ? (
        <button
          onClick={onOpenStorm}
          className={cn(
            /* Navy fill, amande label in BOTH themes (D9) — `--surface-ink`
             * is the panel that does not flip, so the SOS never becomes a
             * pale pill on a pale canvas. */
            'fixed z-50 inline-flex items-center gap-2.5 rounded-full bg-surface-ink text-on-surface-ink',
            'shadow-[0_14px_34px_-10px_rgb(31_45_58/0.35)] transition-transform',
            'hover:-translate-y-0.5',
            'bottom-[76px] right-4 h-12 w-12 justify-center md:bottom-6 md:right-6 md:h-auto md:w-auto md:px-[18px] md:py-3.5',
          )}
          aria-label={t('storm.open')}
        >
          <IconTornado size={20} />
          <span className="hidden text-[13.5px] font-semibold md:inline">{t('storm.open')}</span>
        </button>
      ) : null}
    </div>
  )
}

/** Exact for the root, prefix for everything else — `/resources/x` lights Resources. */
/*
 * Screens where the floating Storm FAB is hidden.
 *
 * These are account-management surfaces with their own sticky footers
 * (drawer Save/Cancel, sticky "Switch to {plan}" bar), and the FAB was
 * literally overlapping them — clicking Save was impossible on the
 * crew edit drawer. The Storm control still exists on these screens,
 * in the top bar; only the redundant floating pill goes away.
 */
function isSettingsPath(pathname: string): boolean {
  const stripped = pathname.replace(/^\/en(\/|$)/, '/')
  return stripped === '/profile' || stripped.startsWith('/billing')
}

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to)
}

/**
 * A nav item is a LINK, not a button.
 *
 * It was a button, and that quietly cost three things a link gives free:
 * cmd/middle-click to open in a new tab, the status-bar URL preview on hover,
 * and the right role — a `navigation` landmark full of buttons tells a screen
 * reader user these are actions rather than destinations.
 *
 * `Architecture_And_Plan.md` is explicit that routes are real and linkable;
 * this is the part of the shell that was not honouring it. The click handler
 * stays for the client-side navigation, and `href` makes it a real link for
 * everything else.
 */
/**
 * `.nav-item-badge` — the cohort month, and nothing else so far.
 *
 * Its own component so the query lives with the one item that needs it. Renders
 * nothing until the cohort read answers, and nothing at all for a TSP member
 * between cohorts — a badge reading "M0" would be worse than no badge.
 *
 * ── Not a pill, deliberately ──────────────────────────────────────────────
 * `.nav-item-badge` has no rule anywhere in the handoff's CSS: `tsp-app.jsx`
 * puts `M6` in a span and it renders as plain nav-item text at the inherited
 * size, sitting at the end of the row. We drew a filled `--cream-dark` capsule,
 * which is the treatment the design reserves for the *counted* badges — the
 * announcements count and the challenge `1` — and those mean "there is
 * something here for you". `M6` counts nothing and asks for nothing; it is the
 * label finishing its own sentence, and a pill made it read as an alert on the
 * one nav row a TSP member sees every time they look at the rail.
 */
function CohortMonthBadge() {
  const cohort = useMyCohort(http)
  if (!cohort.data) return null

  return (
    <span className="ms-auto font-num text-[13px] tabular-nums text-inherit">
      M{cohort.data.month}
    </span>
  )
}

/**
 * `.nav-item-badge` — the mock's `1` beside Weekly Challenge.
 *
 * The prototype drives it from a client-side `challengeUnseen` flag, which it
 * can afford because it has no server. The real signal for "there is something
 * for you here today" is `streak.loggedToday` on the running period: a member
 * who has already logged today has nothing outstanding, and one who has not is
 * exactly who the badge is for. No period running, no badge — a "1" pointing at
 * a screen with nothing on it is worse than silence.
 *
 * Reads the same query as Home's challenge card, so this is a cache hit rather
 * than a second request.
 */
function ChallengeBadge() {
  const challenge = useChallenge(http, useContentLocale())
  // The hook unwraps the `{ period }` envelope; `data` is the period itself,
  // and is undefined both while loading and when none is running.
  const period = challenge.data
  if (!period || period.streak.loggedToday) return null

  return (
    <span className="ms-auto grid h-[18px] min-w-[18px] place-items-center rounded-full bg-surface-ink px-1 font-num text-[11px] font-bold tabular-nums text-on-surface-ink">
      1
    </span>
  )
}

function NavLink({
  item,
  active,
  onClick,
  badge,
}: {
  item: NavItem
  active: boolean
  onClick: () => void
  badge?: React.ReactNode
}) {
  const { t } = useTranslation()
  const Icon = ICONS[item.icon]

  return (
    <a
      href={hrefInCurrentLocale(item.to)}
      onClick={(event) => {
        // Let the browser handle modified clicks — that is the whole point of
        // being a link. Only a plain left click is intercepted for the router.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onClick()
      }}
      aria-current={active ? 'page' : undefined}
      /*
       * `.nav-item` — and the active state is the one that was wrong.
       *
       * The design fills the current item with `--ink` and puts `--cream` text
       * on it: a solid dark pill, unmistakable at a glance down the rail. We had
       * a pale `cream-dark` tint, which is the design's HOVER colour — so the
       * item you were on looked exactly like the item under your cursor, and the
       * sidebar never told you where you were.
       */
      className={cn(
        'flex min-h-[44px] items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-medium transition-colors',
        active
          ? 'bg-ink text-cream'
          : 'text-ink-soft hover:bg-canvas hover:text-ink',
      )}
    >
      <Icon size={20} />
      {t(`nav.${item.labelKey}`)}
      {badge}
    </a>
  )
}

/**
 * A rail item that is not in `NAV_BY_ROLE`.
 *
 * Separate from `NavLink` because the nav table is keyed on `labelKey` and
 * carries an icon NAME resolved through `ICONS` — a table that exists so adding
 * a platform role fails to compile until somebody decides what it sees. These
 * three are not per-role destinations; folding them in would mean either
 * repeating them in every role's list or teaching the table about exceptions.
 *
 * Same link semantics: a real `href`, modified clicks left to the browser.
 */
function SecondaryLink({
  to,
  icon,
  label,
  active,
  onNavigate,
}: {
  to: string
  icon: React.ReactNode
  label: string
  active: boolean
  onNavigate: (to: string) => void
}) {
  return (
    <a
      href={hrefInCurrentLocale(to)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onNavigate(to)
      }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-[44px] items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[13.5px] font-medium transition-colors',
        active
          ? 'bg-ink text-cream'
          : 'text-ink-soft hover:bg-canvas hover:text-ink',
      )}
    >
      {icon}
      {label}
    </a>
  )
}

/** `.more-item` — a tile in the More sheet's grid. Same link semantics as NavLink. */
function MoreTile({
  item,
  active,
  onClick,
}: {
  item: NavItem
  active: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const Icon = ICONS[item.icon]

  return (
    <a
      href={hrefInCurrentLocale(item.to)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onClick()
      }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-[76px] flex-col justify-center gap-2 rounded-[var(--r)] border px-3.5 py-3 text-[13.5px] font-medium transition-colors',
        active
          ? 'border-transparent bg-ink text-cream'
          : 'border-border bg-surface text-ink-soft hover:border-border-deep hover:text-ink',
      )}
    >
      <Icon size={22} />
      <span>{t(`nav.${item.labelKey}`)}</span>
    </a>
  )
}

function TabLink({
  item,
  active,
  onClick,
}: {
  item: NavItem
  active: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const Icon = ICONS[item.icon]

  return (
    <a
      href={hrefInCurrentLocale(item.to)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onClick()
      }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-[50px] flex-1 flex-col items-center justify-center gap-0.5 py-2',
        active ? 'text-ink' : 'text-muted',
      )}
    >
      <Icon size={20} />
      <span className="text-[10px]">{t(`nav.${item.labelKey}`)}</span>
    </a>
  )
}

function TopBar({ onOpenStorm }: { onOpenStorm: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [bellOpen, setBellOpen] = useState(false)
  const feed = useNotifications(http)
  const unreadCount = feed.data?.unread ?? 0

  return (
    /*
     * `.topbar` — 60px on mobile, `--topbar-h` above it, and the glass is
     * `blur(14px) saturate(1.2)`. The saturation is not decoration: blurring a
     * cream canvas without it greys the tint out, and the bar stops reading as
     * the same surface as the page under it.
     */
    <header className="sticky top-0 z-20 flex h-[60px] items-center gap-3 border-b border-border bg-canvas/85 px-4 backdrop-blur-[14px] backdrop-saturate-[1.2] md:h-[var(--topbar-h)] md:gap-[18px] md:px-9 lg:px-14">
      <div className="md:hidden">
        <Logo size={26} withWordmark={false} />
      </div>

      {/*
       * `.search-bar` — a 42px pill capped at 640px on desktop to match the
       * mock, with an enter-arrow chip on the right. Placeholder describes
       * what the field actually searches — courses, community, podcasts —
       * rather than reading "Mat Bot", which suggested a chat with the
       * assistant only and not the whole library.
       */}
      <form
        role="search"
        /*
         * Submitted through the router, never as a native GET: `action` would
         * resolve against the origin and drop the locale basepath, so a search
         * typed on an /en page landed on the French /search. The router's own
         * basepath carries the locale.
         */
        onSubmit={(event) => {
          event.preventDefault()
          const field = event.currentTarget.elements.namedItem('q')
          const q = field instanceof HTMLInputElement ? field.value.trim() : ''
          void navigate({ to: '/search', search: q ? { q } : {} })
        }}
        /*
         * Dark mode pins the search bar to `--cream-dark` (mock line
         * 1645). Without it, `bg-surface` flips to the raised navy card
         * fill, which reads too close to the topbar's own translucent
         * navy and the input loses its edge. `--cream-dark` is one shade
         * up — a slightly warmer navy that separates it cleanly.
         */
        className="flex h-[42px] min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-surface pe-1.5 ps-3.5 transition-[border-color,box-shadow] focus-within:border-ink focus-within:shadow-[0_0_0_3px_rgb(41_51_57/0.12)] dark:bg-cream-dark md:max-w-[640px]"
      >
        {/*
         * `.search-go` — the LEADING magnifier is the submit control, and the
         * trailing slot is a `kbd` hint. We had it inverted: a decorative
         * magnifier on the left and an arrow button on the right.
         *
         * The mock's arrangement is the better one and not only for parity.
         * The magnifier is where every browser and every other product puts
         * the thing you press to search, and the right-hand slot then teaches
         * the keyboard route instead of duplicating the mouse one — a hint,
         * not a second button competing with the first.
         */}
        <button
          type="submit"
          aria-label={t('search.submit')}
          className="-ms-1 grid h-8 w-8 flex-none place-items-center rounded-full text-muted transition-colors hover:bg-cream-dark hover:text-ink"
        >
          <IconSearch size={16} title={null} />
        </button>
        <label className="sr-only" htmlFor="topbar-search">
          {t('search.placeholder')}
        </label>
        <input
          id="topbar-search"
          name="q"
          type="search"
          placeholder={t('search.placeholder')}
          className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted"
        />
        {/*
         * Decorative, and hidden from assistive tech: "press Enter to submit"
         * is what a form already promises, so announcing a return glyph here
         * is noise rather than information. Hidden on touch, where there is no
         * physical key to press.
         */}
        <kbd
          aria-hidden
          className="me-1 hidden flex-none rounded-[6px] border border-border bg-canvas px-1.5 py-0.5 font-sans text-[11px] leading-[1.4] text-muted md:block"
        >
          ↵
        </kbd>
      </form>

      {/* `.topbar-actions { margin-left: auto }` — with the search capped, the
       * actions belong on the far edge rather than trailing it. */}
      <div className="ms-auto flex items-center gap-1">
        {/*
         * FR · EN, ahead of the icon cluster. The switcher lived only inside
         * the profile menu, which on a bilingual Québec product made changing
         * language a two-step hunt — the first QA round filed it as «no
         * visible language switcher inside the app». Desktop only: the mobile
         * topbar is already full, and the profile menu keeps its row.
         */}
        <div className="hidden md:block">
          <CompactLanguageToggle />
        </div>

        {/*
         * Order in the mock (`topbar-panels.jsx`): megaphone → bell →
         * calendar → divider → Storm. Megaphone comes FIRST because
         * broadcast reads more prominently than personal activity in
         * this product — the bell picks up the small unread dot instead
         * of a count. Was reversed, which put personal activity above
         * platform news.
         */}
        <AnnouncementsButton />

        {/*
         * `.tb-panel` anchor. Relative, so the popover positions against the
         * BELL rather than the topbar — anchored to the control it belongs to
         * is what keeps it pointing at the right thing on a narrow screen.
         */}
        <div className="relative">
          <button
            onClick={() => setBellOpen((open) => !open)}
            aria-expanded={bellOpen}
            aria-haspopup="dialog"
            className="relative flex h-10 w-10 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-cream-dark hover:text-ink"
            aria-label={
              unreadCount > 0
                ? `${t('notifications.title')} — ${t('notifications.unreadCount', { count: unreadCount })}`
                : t('notifications.title')
            }
          >
            <IconBell size={20} />
            {/*
             * The count, not a dot (8 Oct 2026): the client asked to see how
             * many are waiting. `unread` is the whole feed's, never the page's.
             * Drawn exactly as the megaphone's, so the two read as one family.
             */}
            {unreadCount > 0 ? (
              <span
                aria-hidden
                className="absolute right-1.5 top-1.5 grid h-[17px] min-w-[17px] place-items-center rounded-full bg-surface-ink px-1 text-[10px] font-bold text-on-surface-ink"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </button>
          {bellOpen ? <NotificationsPanel onClose={() => setBellOpen(false)} /> : null}
        </div>

        {/* `.tb-calendar` — what is coming up. The events list, from anywhere. */}
        <Link
          to="/events"
          className="hidden h-10 w-10 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-cream-dark hover:text-ink md:flex"
          aria-label={t('nav.events')}
        >
          <IconCalendar size={20} title={null} />
        </Link>

        {/*
         * `.tb-divider` — the vertical rule in the mock that separates
         * the icon cluster from the Storm pill. Without it Storm reads
         * as a fourth icon rather than as the one filled control the
         * bar is built around.
         */}
        <span aria-hidden className="mx-2 hidden h-6 w-px bg-border md:block" />

        {/*
         * `.tb-storm` — a LABELLED dark pill, not a bare icon.
         *
         * The design gives this the only filled treatment in the topbar, and
         * the word beside the glyph is the point: a lone tornado is a puzzle at
         * the moment somebody needs it least ambiguous. The mobile FAB carries
         * the same label for the same reason.
         */}
        <button
          onClick={onOpenStorm}
          className="hidden h-10 items-center gap-2 rounded-full bg-surface-ink px-4 text-[13.5px] font-semibold text-on-surface-ink transition-transform hover:-translate-y-px md:inline-flex"
        >
          <IconTornado size={17} title={null} />
          {t('storm.short')}
        </button>
      </div>
    </header>
  )
}

/**
 * `.tb-megaphone` — official announcements, with a count badge when the
 * ticker holds live items.
 *
 * The bell counts things about *you*; this counts what the team has
 * broadcast. Different endpoints, different read semantics — separating
 * them is why both live in the top bar (the mock is explicit on that
 * point). The badge reads from `useAnnouncementTicker`, which is already
 * fetched by `Ticker` on the home page, so this is a cache hit rather
 * than a second request wherever both are mounted.
 */
function AnnouncementsButton() {
  const { t, i18n } = useTranslation()
  const ticker = useAnnouncementTicker(http, i18n.language === 'en' ? 'en' : 'fr')
  const count = ticker.data?.items.length ?? 0

  return (
    <Link
      to="/announcements"
      className="relative flex h-10 w-10 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-cream-dark hover:text-ink"
      aria-label={t('nav.announcements')}
    >
      <IconMegaphone size={20} title={null} />
      {count > 0 ? (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 grid h-[17px] min-w-[17px] place-items-center rounded-full bg-surface-ink px-1 text-[10px] font-bold text-on-surface-ink"
        >
          {count > 9 ? '9+' : count}
        </span>
      ) : null}
    </Link>
  )
}

/**
 * A sidebar/tab href a browser can follow on its own.
 *
 * Plain clicks are intercepted and routed (the basepath carries the locale),
 * but a cmd-click, «copy link address» or middle-click uses the raw `href` —
 * and the raw path loses the /en prefix, which is how the Q&A link opened the
 * French page from an English session in the QA round. The document language
 * is what the interface is rendered in, so it is what the href promises.
 */
function hrefInCurrentLocale(to: string): string {
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : 'fr-CA'
  return pathInLocale(to, lang.startsWith('en') ? 'en' : 'fr')
}
